import React from 'react';
import styled from 'styled-components';
import { DefaultImages } from 'shared/constants';
import { isValidThemeHex, resolveConciergeTheme } from '@dutchie/common/utils/concierge-luminance';
import { PhoneWrapper } from 'src/svg/phone-wrapper';
import { Sparkles } from 'src/svg/sparkles';
import { CONCIERGE_PREVIEW_CSS } from './concierge-preview-css';

type ConciergePreviewProps = {
  accentColorHex?: string;
  colorScheme: 'DARK' | 'LIGHT';
  canvasColorHex?: string;
};

// Same stock product photo set the menu preview's product cards fall back to.
const PRODUCT_PHOTO = DefaultImages.Indica ?? DefaultImages.Flower ?? '';

function conciergePreviewScheme(canvasColorHex: string): 'dark' | 'light-max-contrast' | 'light' {
  const { scheme, ink } = resolveConciergeTheme(canvasColorHex);
  if (scheme === 'DARK') {
    return 'dark';
  }

  return ink === 'MAX_CONTRAST' ? 'light-max-contrast' : 'light';
}

function PersonIcon(): JSX.Element {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
      <circle cx='12' cy='8' r='4' />
      <path d='M5 20c1.6-3.6 4.4-5 7-5s5.4 1.4 7 5' />
    </svg>
  );
}

function BagIcon(): JSX.Element {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinejoin='round'>
      <path d='M6 8h12l-1.2 12H7.2L6 8z' />
      <path d='M9 10V6a3 3 0 0 1 6 0v4' />
    </svg>
  );
}

export function ConciergePreview({ accentColorHex, colorScheme, canvasColorHex }: ConciergePreviewProps): JSX.Element {
  const vars: Record<string, string> = {};
  if (isValidThemeHex(accentColorHex)) vars['--dcm-concierge-accent'] = accentColorHex;
  if (isValidThemeHex(canvasColorHex)) vars['--dcm-concierge-canvas'] = canvasColorHex;

  let previewScheme: 'dark' | 'light-max-contrast' | 'light';
  if (isValidThemeHex(canvasColorHex)) {
    previewScheme = conciergePreviewScheme(canvasColorHex);
  } else {
    previewScheme = colorScheme === 'LIGHT' ? 'light' : 'dark';
  }

  return (
    <Shell>
      <PhoneWrapper />
      <Root
        data-testid='concierge-preview'
        data-scheme={previewScheme}
        data-has-canvas={isValidThemeHex(canvasColorHex) ? 'true' : 'false'}
        data-has-accent={isValidThemeHex(accentColorHex) ? 'true' : 'false'}
        style={vars}
      >
        <Header>
          <Logo aria-hidden />
          <HeaderActions>
            <IconCircle aria-hidden>
              <PersonIcon />
            </IconCircle>
            <IconPill aria-hidden>
              <BagIcon />
              <span>2</span>
            </IconPill>
          </HeaderActions>
        </Header>

        <MenuTypeBar>
          <MenuTypeToggle>
            <MenuTypeActive>Rec</MenuTypeActive>
            <MenuTypeInactive>Med</MenuTypeInactive>
          </MenuTypeToggle>
        </MenuTypeBar>

        <Chat>
          <Assistant>
            <SparkleBadge aria-hidden>
              <Sparkles />
            </SparkleBadge>
            <span>What strain type are you in the mood for?</span>
          </Assistant>

          <Chips>
            <Chip>Indica</Chip>
            <Chip>Sativa</Chip>
            <Chip>Hybrid</Chip>
          </Chips>

          <User>Indica</User>

          <Assistant>
            <SparkleBadge aria-hidden>
              <Sparkles />
            </SparkleBadge>
            <span>Top indica on the rec menu right now:</span>
          </Assistant>

          <Card>
            <PickBadge>Concierge pick</PickBadge>
            <ProductRow>
              <ProductImage src={PRODUCT_PHOTO} alt='' />
              <ProductInfo>
                <ProductName>Trichome Farms | Truffle Treats</ProductName>
                <ProductMeta>
                  <WeightPill>1g</WeightPill>
                  <Price>$14.59</Price>
                  <StrainLabel>Indica</StrainLabel>
                </ProductMeta>
              </ProductInfo>
            </ProductRow>
            <ProductNote>Heavy indica at 28% THC — great for relaxing or winding down at night.</ProductNote>
            <EffectsRow>
              <EffectsLabel>Effects</EffectsLabel>
              <EffectChip>Relaxed</EffectChip>
              <EffectChip>Sleepy</EffectChip>
              <EffectChip>Happy</EffectChip>
            </EffectsRow>
            <AddToCart type='button'>Add to cart</AddToCart>
          </Card>

          <FollowUp>There&apos;s also a 30% pickup discount on Trichome Farms — want me to apply it?</FollowUp>
        </Chat>

        <Composer>
          <InputField>
            <InputPlaceholder>Ask about products, effects, deals…</InputPlaceholder>
            <SendCircle aria-hidden>↑</SendCircle>
          </InputField>
          <ResetCircle aria-hidden>↻</ResetCircle>
        </Composer>
      </Root>
    </Shell>
  );
}

/* Same device-shell geometry as the menu preview (components/preview/preview.tsx). */
const Shell = styled.div`
  height: 802px;
  max-width: 370px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const Root = styled.div`
  ${CONCIERGE_PREVIEW_CSS}
  background: var(--dcm-color-body-bg);
  color: var(--dcm-color-text-primary);
  border-radius: 44px;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  left: 11px;
  overflow: hidden;
  position: absolute;
  right: 11px;
  top: 11px;
  /* Old school fix for Safari not respecting border radius clipping */
  transform: translate3d(0, 0, 0);
`;

const Header = styled.div`
  align-items: center;
  background: var(--dcm-color-surface);
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  padding: 34px 14px 10px;
`;

const Logo = styled.span`
  background: var(--dcm-color-surface-hover);
  border: 1px solid var(--dcm-color-border);
  border-radius: 999px;
  display: block;
  height: 34px;
  width: 34px;
`;

const HeaderActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;

  svg {
    height: 14px;
    width: 14px;
  }
`;

const IconCircle = styled.span`
  align-items: center;
  border: 1px solid var(--dcm-color-border);
  border-radius: 999px;
  display: flex;
  height: 30px;
  justify-content: center;
  width: 30px;
`;

const IconPill = styled.span`
  align-items: center;
  border: 1px solid var(--dcm-color-border);
  border-radius: 999px;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 5px;
  height: 30px;
  padding: 0 12px;
`;

const MenuTypeBar = styled.div`
  background: var(--dcm-color-surface);
  border-bottom: 1px solid var(--dcm-color-border);
  flex-shrink: 0;
  padding: 4px 14px 10px;
`;

const MenuTypeToggle = styled.div`
  align-items: center;
  border: 1px solid var(--dcm-color-border);
  border-radius: 999px;
  display: inline-flex;
  padding: 2px;
`;

const MenuTypeActive = styled.span`
  background: var(--dcm-concierge-accent);
  border-radius: 999px;
  color: var(--dcm-color-text-inverted);
  font-size: 12px;
  font-weight: 700;
  padding: 4px 14px;
`;

const MenuTypeInactive = styled.span`
  color: var(--dcm-color-text-muted);
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
`;

const Chat = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  overflow: hidden;
  padding: 16px 14px;
`;

/* Agent messages render as plain text beside a sparkles avatar, not a bubble. */
const Assistant = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SparkleBadge = styled.span`
  background: var(--dcm-concierge-accent-glow);
  color: var(--dcm-concierge-accent);
  align-items: center;
  border-radius: 999px;
  display: flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  width: 24px;

  svg {
    height: 11px;
    width: 11px;
  }
`;

const User = styled.div`
  background: var(--dcm-color-surface-hover);
  border: 1px solid var(--dcm-concierge-accent-soft);
  border-radius: 12px 12px 4px 12px;
  padding: 10px 14px;
  align-self: flex-end;
  max-width: 85%;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

/* Outlined variant, matching the client's suggestion chips and secondary buttons. */
const Chip = styled.span`
  background: transparent;
  border: 1px solid var(--dcm-concierge-accent-soft);
  border-radius: 999px;
  color: var(--dcm-concierge-accent);
  font-size: 12px;
  font-weight: 700;
  padding: 6px 12px;
`;

const Card = styled.div`
  background: var(--dcm-color-surface);
  border: 1px solid var(--dcm-concierge-accent-soft);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
`;

const PickBadge = styled.span`
  align-self: flex-start;
  background: var(--dcm-concierge-accent-glow);
  border: 1px solid var(--dcm-concierge-accent-soft);
  border-radius: 999px;
  color: var(--dcm-concierge-accent);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.1em;
  padding: 3px 9px;
  text-transform: uppercase;
`;

const ProductRow = styled.div`
  display: flex;
  gap: 10px;
`;

// No background bed — matches the remote's thumbnails, which let the card
// surface show through the letterboxing (dutchie-cf-agents#211).
const ProductImage = styled.img`
  border-radius: 6px;
  display: block;
  flex-shrink: 0;
  height: 56px;
  object-fit: contain;
  width: 56px;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const ProductName = styled.div`
  font-weight: 700;
`;

const ProductMeta = styled.div`
  align-items: center;
  display: flex;
  font-size: 12px;
  gap: 8px;
`;

const WeightPill = styled.span`
  border: 1px solid var(--dcm-color-border);
  border-radius: 999px;
  padding: 2px 9px;
`;

const Price = styled.span`
  font-weight: 700;
`;

const StrainLabel = styled.span`
  color: var(--dcm-color-text-muted);
`;

const ProductNote = styled.div`
  color: var(--dcm-concierge-accent);
  font-size: 12px;
  font-style: italic;
  font-weight: 600;
  letter-spacing: 0.01em;
`;

const EffectsRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const EffectsLabel = styled.span`
  color: var(--dcm-color-text-secondary);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

// Mirrors the remote's EffectChip (product-atoms.tsx): a quiet accent-derived
// secondary (hue +90°, low chroma) with scheme-flipped ink — all via the
// --dcm-concierge-effect-* tokens.
const EffectChip = styled.span`
  background: var(--dcm-concierge-effect-tint);
  border: 1px solid var(--dcm-concierge-effect-border);
  border-radius: 999px;
  color: var(--dcm-concierge-effect-ink);
  font-size: 11px;
  font-weight: 600;
  padding: 2px 9px;
`;

const AddToCart = styled.button`
  background: var(--dcm-color-primary);
  border: none;
  border-radius: 999px;
  color: var(--dcm-color-text-inverted);
  font-size: 13px;
  font-weight: 700;
  padding: 10px;
  width: 100%;
`;

/* Continuation of the same agent turn — text column only, no avatar. */
const FollowUp = styled.div`
  padding-left: 34px;
`;

const Composer = styled.div`
  align-items: center;
  background: var(--dcm-color-surface);
  border-top: 1px solid var(--dcm-color-border);
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  padding: 12px 14px;
`;

const InputField = styled.div`
  align-items: center;
  border: 1px solid var(--dcm-color-border);
  border-radius: 999px;
  display: flex;
  flex: 1;
  gap: 8px;
  min-width: 0;
  padding: 5px 5px 5px 14px;
`;

const InputPlaceholder = styled.span`
  color: var(--dcm-color-text-secondary);
  flex: 1;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SendCircle = styled.span`
  align-items: center;
  background: var(--dcm-concierge-accent);
  border-radius: 999px;
  color: var(--dcm-color-text-inverted);
  display: flex;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  height: 26px;
  justify-content: center;
  width: 26px;
`;

const ResetCircle = styled.span`
  align-items: center;
  border: 1px solid var(--dcm-color-border);
  border-radius: 999px;
  display: flex;
  flex-shrink: 0;
  font-size: 14px;
  height: 34px;
  justify-content: center;
  width: 34px;
`;
